import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F8E8] to-[#F5FAF0]">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center text-darkforest-700 hover:text-darkforest-800">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Return to Home
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="card p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-gray-500 text-sm mb-6">A(rDx)HD app and website</p>
            
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-lg mb-6">
                <strong>Effective Date:</strong> July 18, 2025. <strong>Last updated:</strong> February 2026.
              </p>

              <p className="mb-6">
                This Privacy Policy ("Policy") describes how the A(rDx)HD Platform ("Platform," "we," "our," or "us") collects, uses, protects, and discloses user and device data when you use our app or website. This policy applies to the A(rDx)HD application available on the web and on Google Play. By accessing or using the Platform, you agree to this Policy.
              </p>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Target audience and content ratings</h2>
                <p className="mb-4">
                  The A(rDx)HD app is rated for all ages. Our content ratings are: <strong>Brazil (ClassInd): All ages</strong>; <strong>North America (ESRB): Everyone</strong>; <strong>Europe (PEGI): PEGI 3</strong>; <strong>Germany (USK): USK All ages</strong>; <strong>Rest of world (IARC) / Russia / South Korea (Google Play): Rated for 3+</strong>. We do not use content descriptors. The in-app content (games, text, and visuals) is designed to be appropriate for all ages and free of violence, fear, or other content that would require a higher rating. We comply with the requirements of these rating authorities and with applicable children's privacy laws as described in Section 10.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Purpose of the Platform</h2>
                <p className="mb-4">
                  The Platform is designed to track attention and cognitive performance through interactive games and activities. Content, results, and recommendations are for your awareness and to support your own follow-up with professionals you choose. We use data only as described in this Policy to deliver and improve these services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Information We Collect</h2>
                <p className="mb-4">We collect the following categories of data:</p>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-800 mb-2">A. Account and profile information (you provide):</h3>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Name and email address (for account registration and communication)</li>
                      <li>Age and region (for consent and legal compliance)</li>
                      <li>Consent choices (e.g., terms, privacy, assessment, research participation)</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-800 mb-2">B. Activity and assessment data:</h3>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Responses to in-app questions and game interactions</li>
                      <li>Game progress and results (scores, performance data)</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-800 mb-2">C. Technical and device information (automatically collected):</h3>
                    <ul className="list-disc list-inside space-y-1">
                      <li>IP address, browser type, device type</li>
                      <li>Session timestamps and page interactions</li>
                      <li>Cookies and similar technologies (see Section 10)</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">4. How We Use Your Information</h2>
                <p className="mb-4">We process data only as necessary to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Deliver and personalize user experience and feedback</li>
                  <li>Inform users of their results and support their own follow-up</li>
                  <li>Improve the functionality, design, and reliability of our tools</li>
                  <li>Enforce platform security, detect abuse, and comply with applicable laws</li>
                  <li>Communicate essential updates related to your account</li>
                </ul>
                <p className="mt-4 text-sm text-gray-600">
                  All usage is subject to strict data minimization and purpose limitation standards.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
                <p className="mb-4">We implement industry-leading safeguards to protect user information, including:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>End-to-end encryption</li>
                  <li>Secure server infrastructure (HTTPS, firewalls, and physical security)</li>
                  <li>Role-based access controls</li>
                  <li>Regular internal audits and vulnerability scans</li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-4">
                  <p className="text-forest-800">
                    <strong>Note:</strong> Despite best efforts, no digital system is completely immune to risk. By using the Platform, you acknowledge this inherent limitation and accept that no absolute guarantee can be provided.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Information Sharing and Disclosure</h2>
                <p className="mb-4">We do not sell, rent, or trade your personal data. We do not share your data with third parties for advertising or marketing.</p>
                <h3 className="font-medium text-gray-800 mb-2 mt-4">Third parties and service providers</h3>
                <p className="mb-2">We use the following service providers to operate the app and store data. These parties process data on our behalf under contract and are not permitted to use your data for their own purposes:</p>
                <ul className="list-disc list-inside space-y-1 mb-4">
                  <li><strong>Google Firebase</strong> (authentication, database, hosting): Your account data, consent records, and game/assessment data are stored and processed by Google Firebase. Google's privacy practices apply to their processing: <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" className="text-darkforest-700 hover:underline">Firebase Privacy</a>.</li>
                </ul>
                <p className="mb-4">We may also disclose user information only in these limited circumstances:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>With your explicit, informed consent (e.g., as described in the consent form)</li>
                  <li>To comply with legal obligations, subpoenas, or court orders</li>
                  <li>When necessary to protect the rights, safety, or property of users, staff, or the public</li>
                  <li>For research or product improvement in fully anonymized, aggregated form that cannot identify you</li>
                </ul>
                <p className="mt-4 text-sm text-gray-600">
                  All disclosures are reviewed for necessity and minimized in scope.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Legal Basis for Processing (For GDPR Users)</h2>
                <p className="mb-4">If you are located in the European Economic Area, we process your personal data under the following legal bases:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Consent (Article 6(1)(a) GDPR)</li>
                  <li>Contractual necessity (Article 6(1)(b))</li>
                  <li>Compliance with legal obligations (Article 6(1)(c))</li>
                  <li>Legitimate interests (Article 6(1)(f)), such as platform security and educational research, unless overridden by your privacy rights</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Your Privacy Rights</h2>
                <p className="mb-4">Depending on where you live, you may have the right to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
                  <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
                  <li><strong>Object or restrict:</strong> Limit or object to certain processing</li>
                  <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                  <li><strong>Withdraw consent:</strong> Revoke consent at any time (without affecting past lawful processing)</li>
                </ul>
                <p className="mt-4">
                  <strong>Request account or data deletion:</strong> You may request deletion of your account and data at any time through our dedicated page: <Link to="/delete" className="text-darkforest-700 hover:text-darkforest-800 underline">Request account or data deletion</Link>. You can also contact us by email to request deletion or other rights.
                </p>
                <p className="mt-4 text-sm text-gray-600">
                  To exercise any of these rights, contact us at ardhxhd@gmail.com. We will respond within the time required by applicable law.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Data Retention</h2>
                <p className="mb-4">We retain personal data only as long as:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Necessary to provide services</li>
                  <li>Required by law (e.g., audit logs)</li>
                  <li>Consented to by the user</li>
                </ul>
                <p className="mt-4">
                  When retention is no longer required, data is securely deleted or anonymized in accordance with industry best practices.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Cookies and Tracking Technologies</h2>
                <p className="mb-4">We use cookies and similar technologies for:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Session management</li>
                  <li>Platform performance analysis</li>
                  <li>Feature optimization and user preferences</li>
                </ul>
                <p className="mt-4">
                  You may manage cookie settings through your browser or reject optional analytics cookies via in-app options (where available).
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Children's Privacy</h2>
                <p className="mb-4">
                  The A(rDx)HD app content is rated for all ages (ESRB Everyone, PEGI 3, and equivalent ratings as set out in Section 1). Our in-app content contains no violence, fear, or other material that would require a higher age rating.
                </p>
                <p className="mb-4">
                  Our app and website are not directed at children under 13. We do not knowingly collect personal information from children under 13 without verifiable parental consent. If we learn that we have collected personal information from a child under 13 without parental consent, we will delete it promptly.
                </p>
                <p className="mb-4">
                  Users between 13 and 17 must have parental or guardian permission to use the Platform. Our consent flow requires users under 18 to confirm that a parent or guardian has reviewed the information and consents to participation.
                </p>
                <p>
                  If you believe a minor has provided personal information without appropriate consent, contact us at ardhxhd@gmail.com and we will remove it promptly.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">12. International Data Transfers</h2>
                <p>
                  Our servers may be located in jurisdictions outside your home country. By using the Platform, you acknowledge and consent to the transfer of your data internationally. We use Standard Contractual Clauses and other approved mechanisms where applicable to safeguard international data transfers under GDPR and similar frameworks.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Limitation of Liability</h2>
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
                  <p className="text-red-800">
                    <strong>Important Disclaimer:</strong> To the fullest extent permitted by law:
                  </p>
                </div>
                <ul className="list-disc list-inside space-y-2">
                  <li>The Platform, its developers, and its affiliates shall not be liable for any direct, indirect, incidental, or consequential damages arising from the use or misuse of the Platform or reliance on any content or output generated.</li>
                  <li>Use of this Platform does not establish a provider-patient relationship.</li>
                  <li>All users agree to hold harmless and indemnify the Platform and its operators from any claims, legal actions, or disputes resulting from use of the Platform or reliance on its content.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">14. Policy Updates</h2>
                <p>
                  We reserve the right to update this Policy to reflect legal, technological, or operational changes. Users will be notified of significant changes via email or in-app notice. Continued use of the Platform after such notice constitutes acceptance of the updated terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">15. Contact Us</h2>
                <p className="mb-4">
                  To ask questions or submit requests related to this Policy or your data rights, contact:
                </p>
                <div className="mt-4 space-y-2">
                  <p>
                    <strong>Email:</strong> 
                    <a href="mailto:ardhxhd@gmail.com" className="text-darkforest-700 hover:text-darkforest-800 underline ml-2">
                      ardhxhd@gmail.com
                    </a>
                  </p>
                  <p>
                    <strong>Data Protection Officer:</strong> 
                    <a href="mailto:ardhxhd@gmail.com" className="text-darkforest-700 hover:text-darkforest-800 underline ml-2">
                      ardhxhd@gmail.com
                    </a>
                  </p>
                </div>
              </section>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPage; 